import { jsx, jsxs } from "react/jsx-runtime";
import { string, bool, func, oneOfType, element, number } from "prop-types";
import React, { useState, useEffect, forwardRef, useContext } from "react";
import Label from "@ingka/label";
import Button from "@ingka/button";
import cx from "classnames";
import visibilityHide from "@ingka/ssr-icon/paths/visibility-hide";
import visibilityShow from "@ingka/ssr-icon/paths/visibility-show";
import SSRIcon from "@ingka/ssr-icon";
import { FormFieldContext } from "@ingka/form-field";
const propTypes = {
  /** Unique ID of the input field (required) */
  id: string.isRequired,
  /** Text that is passed to the Label component */
  label: oneOfType([string, element]),
  /** Prevents adding placeholder attribute on `<input>` */
  placeholder: string,
  /** Sets field as required and aria-required */
  req: oneOfType([bool, string, element]),
  /** Type attribute of the input (text or password) (required) */
  type: string.isRequired,
  /** Disabled state of the input field */
  disabled: bool,
  /** Readonly state of the input field */
  readOnly: bool,
  /** Trailing icon button click description */
  iconClickDescription: oneOfType([string, element]),
  /** 'leading' or 'trailing' */
  iconPosition: string,
  /** Trailing icon button onClick function */
  iconOnClick: func,
  /** Function onChange for `<input>` */
  onChange: func,
  /** Default value for the component, makes component Uncontrolled */
  defaultValue: oneOfType([string, number]),
  /** Value for the component, makes component Controlled */
  value: oneOfType([string, number]),
  /** Sets aria-describedby on input field */
  describedById: string,
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Text used for screenreaders for hide password button (type="password") */
  hidePasswordText: oneOfType([string, element]),
  /** Text used for screenreaders for show password button (type="password") */
  showPasswordText: oneOfType([string, element]),
  /** Function returning react element with paths for SSRIcon from `@ingka/ssr-icon/paths/[icon].js` */
  ssrIcon: func,
  /** Can be used to add classes to base HTML element. */
  className: string,
  prefixLabel: oneOfType([string, element]),
  suffixLabel: oneOfType([string, element]),
  /** Sets inputmode attribute if `type !== 'number'` */
  inputMode: string
};
const InputField = forwardRef((props, ref) => {
  const {
    prefix = "",
    type = "text",
    label,
    placeholder,
    req = false,
    inputMode,
    disabled = false,
    readOnly = false,
    hidePasswordText = "Hide password",
    showPasswordText = "Show password",
    className,
    prefixLabel,
    suffixLabel,
    id,
    describedById,
    ssrIcon,
    iconPosition,
    iconOnClick,
    iconClickDescription,
    onChange,
    ...other
  } = props;
  const { shouldValidate, valid, setCharacterCount } = useContext(FormFieldContext);
  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setPasswordVisible(!passwordVisible);
  };
  const onChangeHandler = (e) => {
    if (setCharacterCount) {
      setCharacterCount(e.target.value.length);
    }
    setLabelled(e.target.value !== "");
    if (onChange) {
      onChange(e);
    }
  };
  const { value, defaultValue } = other;
  let defaultCharacterCount = 0;
  let defaultLabelled = false;
  if (value || value === 0) {
    defaultCharacterCount = value.length;
    defaultLabelled = value === 0 || value;
  } else if (defaultValue || defaultValue === 0) {
    defaultLabelled = defaultValue === 0 || defaultValue;
    defaultCharacterCount = defaultValue.length;
  }
  const [labelled, setLabelled] = useState(defaultLabelled);
  const [passwordVisible, setPasswordVisible] = useState(false);
  useEffect(() => {
    setLabelled(defaultLabelled);
  }, [defaultLabelled]);
  useEffect(() => {
    if (setCharacterCount) {
      setCharacterCount(defaultCharacterCount);
    }
  }, [defaultCharacterCount]);
  const trailing = type === "password" || iconPosition === "trailing" && ssrIcon;
  const baseClass = `${prefix}input-field`;
  const classes = cx(
    `${baseClass}`,
    disabled && `${baseClass}--disabled`,
    readOnly && `${baseClass}--read-only`,
    prefixLabel && `${baseClass}--prefixed`,
    suffixLabel && `${baseClass}--suffixed`,
    ssrIcon && iconPosition === "leading" && `${baseClass}--leading-icon`,
    trailing && `${baseClass}--trailing-icon`,
    labelled && `${baseClass}--labelled`,
    shouldValidate && !valid && `${baseClass}--error`,
    shouldValidate && valid && `${baseClass}--success`,
    `${prefix}label-wrapper ${prefix}label-wrapper--text-input`,
    className
  );
  const convertedType = type === "number" ? "text" : type;
  return /* @__PURE__ */ jsxs("div", { className: classes, children: [
    label && /* @__PURE__ */ jsx(Label, { prefix, htmlFor: id, label }),
    /* @__PURE__ */ jsxs("div", { className: `${baseClass}__wrapper`, children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          ref,
          type: passwordVisible ? "text" : convertedType,
          id,
          disabled,
          readOnly,
          required: req,
          ...other,
          onChange: onChangeHandler,
          inputMode: type === "number" ? "numeric" : inputMode,
          "aria-describedby": describedById,
          "aria-required": Boolean(req),
          "aria-invalid": shouldValidate ? !valid : null
        }
      ),
      iconPosition === "leading" && ssrIcon && /* @__PURE__ */ jsx(SSRIcon, { prefix, paths: ssrIcon, className: `${baseClass}__leading-icon` }),
      prefixLabel && /* @__PURE__ */ jsx("span", { className: `${baseClass}__prefix-label`, children: prefixLabel }),
      suffixLabel && /* @__PURE__ */ jsx("span", { className: `${baseClass}__suffix-label`, children: suffixLabel }),
      type === "password" && /* @__PURE__ */ jsx(
        Button,
        {
          htmlType: "button",
          prefix,
          className: `${baseClass}__trailing-icon`,
          iconOnly: true,
          xsmallIconOnly: true,
          ssrIcon: passwordVisible ? visibilityHide : visibilityShow,
          onClick: togglePasswordVisibility,
          type: "tertiary",
          text: passwordVisible ? hidePasswordText : showPasswordText,
          disabled
        }
      ),
      iconPosition === "trailing" && ssrIcon ? /* @__PURE__ */ jsx(
        Button,
        {
          htmlType: "button",
          prefix,
          className: `${baseClass}__trailing-icon`,
          xsmallIconOnly: true,
          ssrIcon,
          iconOnly: true,
          onClick: iconOnClick ? iconOnClick : void 0,
          type: "tertiary",
          text: iconClickDescription,
          disabled
        }
      ) : null,
      /* @__PURE__ */ jsx("span", { className: `${baseClass}__border` })
    ] })
  ] });
});
InputField.propTypes = propTypes;
InputField.displayName = "InputField";
var InputField_default = InputField;
export {
  InputField_default as default
};
